import { useParams, useNavigate, Link } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Edit, History, MessageSquare, Eye, Calendar, User, Loader2, Trash2, ThumbsUp, Clock, MoreHorizontal, List } from "lucide-react";
import { useArticle, useCategories, useDeleteArticle, useComments } from "@/hooks/useKBData";
import { useAuth } from "@/providers/AuthProvider";
import { formatDistanceToNow } from "date-fns";
import { renderArticleHtml } from "@/lib/renderArticle";
import { VersionHistoryDrawer } from "@/components/kb/VersionHistoryDrawer";
import { TableOfContents } from "@/components/kb/TableOfContents";
import { MobileTOC } from "@/components/kb/MobileTOC";
import { ReadViewMentionHover } from "@/components/kb/ReadViewMentionHover";
import { CommentsPanel } from "@/components/comments";
import { cn } from "@/lib/utils";
import { calculateStickyHeadersHeight } from "@/lib/tableOfContents";
import { scrollToElementWithOffset } from "@/lib/scrollUtils";


export function KBArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const articleContentRef = useRef<HTMLDivElement>(null);

  // State for TOC
  const [tocHeadings, setTocHeadings] = useState<any[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>('');

  // State for comments
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  // Fetch article data
  const { data: articleData, isLoading: articleLoading, error: articleError } = useArticle(slug || "");
  const { data: categoriesData } = useCategories();
  const { data: commentsData } = useComments(slug || "");

  // Delete mutation
  const deleteMutation = useDeleteArticle();

  const article = articleData?.article;
  const categories = categoriesData?.categories || [];
  const commentsCount = commentsData?.total || 0;

  // Find the category for this article
  const category = categories.find(cat => cat.id === article?.category_id);

  // Render article HTML safely
  const articleHtml = article ? renderArticleHtml(article) : "";

  // Extract headings and set up intersection observer
  useEffect(() => {
    if (!articleContentRef.current) return;

    const extractHeadings = () => {
      const headingElements = Array.from(articleContentRef.current!.querySelectorAll('h1, h2, h3, h4, h5, h6'));

      const headings = headingElements
        .map((heading, index) => {
          const level = parseInt(heading.tagName.charAt(1));
          const text = heading.textContent?.trim() || '';

          if (!text) return null;

          // Generate ID if not present
          let id = (heading as HTMLElement).id;
          if (!id) {
            id = text
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '')
              + `-${index}`;
            (heading as HTMLElement).id = id;
          }

          return { id, text, level, element: heading as HTMLElement };
        })
        .filter(Boolean);

      setTocHeadings(headings as any[]);
    };

    // Extract headings immediately, then again after timeout for safety
    extractHeadings();
    const timer = setTimeout(extractHeadings, 200);

    return () => clearTimeout(timer);
  }, [articleHtml]);

  // Intersection observer for active heading
  useEffect(() => {
    if (!articleContentRef.current || tocHeadings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.id) {
          setActiveHeading(visible.target.id);
        }
      },
      {
        rootMargin: '-30% 0px -60% 0px',
        threshold: [0.1, 0.5]
      }
    );

    tocHeadings.forEach(heading => {
      if (heading.element) {
        observer.observe(heading.element);
      }
    });

    return () => observer.disconnect();
  }, [tocHeadings]);

  // Handle delete with confirmation
  const handleDelete = async () => {
    if (!article || !slug) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${article.title}"? This action cannot be undone.`
    );

    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(slug);
        navigate("/app/kb");
      } catch (error) {
        console.error("Failed to delete article:", error);
        alert("Failed to delete article. Please try again.");
      }
    }
  };

  if (articleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (articleError || !article) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-4">
          The article you're looking for doesn't exist or has been removed.
        </p>
        <Button onClick={() => navigate("/app/kb")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Knowledge Base
        </Button>
      </div>
    );
  }

  // Helper function to get user initials
  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Article Header */}
      <div className="sticky top-14 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/app/kb")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to articles</span>
          </button>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="ghost" size="sm" className="gap-2 rounded-lg" asChild>
                <Link to={`/app/kb/${article.slug}/edit`}>
                  <Edit className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit</span>
                </Link>
              </Button>
            )}
            <VersionHistoryDrawer
              articleSlug={article.slug}
              trigger={
                <Button variant="ghost" size="sm" className="gap-2 rounded-lg">
                  <History className="h-4 w-4" />
                  <span className="hidden sm:inline">History</span>
                </Button>
              }
            />
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 rounded-lg relative"
              onClick={() => setIsCommentsOpen(true)}
            >
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Comments</span>
              {commentsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {commentsCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" className="rounded-lg">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Delete</span>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile TOC - Inside sticky header container */}
        <MobileTOC
          headings={tocHeadings}
          activeHeading={activeHeading}
          className="lg:hidden"
        />
      </div>

      {/* Article Content */}
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-10">
          <main className="min-w-0">
        {/* Header */}
        <header className="mb-8">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold">
            {category?.name || "Uncategorized"}
          </Badge>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                  {getUserInitials(article.author_name || "Unknown")}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium">{article.author_name}</span>
            </div>
            <span className="text-border">•</span>
            <span>Updated {new Date(article.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            <span className="text-border">•</span>
            <div className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              <span>{(article.views || 0).toLocaleString()} views</span>
            </div>
          </div>
        </header>

            {/* Content */}
            <div className="mx-auto w-full max-w-3xl">
              <div className="
                prose prose-slate max-w-none
                prose-h1:text-3xl sm:prose-h1:text-5xl prose-h1:tracking-tight prose-h1:font-semibold
                prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:tracking-tight prose-h2:font-semibold
                prose-h3:text-xl prose-p:leading-7
                prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                prose-ul:my-4 prose-ol:my-4 prose-li:my-1
                prose-blockquote:border-l-4 prose-blockquote:pl-4 prose-blockquote:italic
                prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                prose-pre:rounded-xl prose-pre:p-4 prose-pre:overflow-x-auto
                prose-img:rounded-xl
                prose-table:my-6 prose-table:block prose-table:overflow-x-auto
                prose-th:font-semibold
              ">
                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: articleHtml }}
                  ref={articleContentRef}
                />
              </div>
            </div>

            {/* DOM-driven hover for mentions in read view */}
            <ReadViewMentionHover containerRef={articleContentRef} />

            {/* Feedback */}
            <div className="mt-12 p-6 rounded-2xl border border-border bg-muted/30">
              <p className="text-sm font-medium mb-3">Was this article helpful?</p>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="gap-2 rounded-lg">
                  <ThumbsUp className="h-4 w-4" />
                  Yes, thanks!
                </Button>
                <Button variant="ghost" size="sm" className="rounded-lg text-muted-foreground">
                  Not really
                </Button>
              </div>
            </div>

          </main>

          {/* Table of Contents Sidebar - Desktop only */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-auto pr-2 space-y-4">
              {/* Desktop TOC - Show as minimal sidebar */}
              {tocHeadings.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide px-3 py-2">
                    <List className="h-3 w-3" />
                    Contents ({tocHeadings.length})
                  </div>
                  <nav>
                    <ul className="space-y-0.5">
                      {tocHeadings.map((heading) => {
                        const isActive = activeHeading === heading.id;
                        const indentLevel = Math.min(heading.level - 1, 3);

                        return (
                          <li key={heading.id}>
                            <button
                              onClick={() => {
                                scrollToElementWithOffset(heading.id, 120);
                              }}
                              className={cn(
                                'text-left w-full px-3 py-1.5 rounded-md text-sm transition-colors hover:bg-muted',
                                'border-l-2 border-transparent hover:border-muted-foreground/20',
                                isActive && 'bg-muted border-primary text-foreground font-medium',
                                !isActive && 'text-muted-foreground',
                              )}
                              style={{
                                paddingLeft: `${0.75 + (indentLevel * 0.75)}rem`,
                                marginLeft: `${indentLevel * 0.5}rem`
                              }}
                              title={heading.text}
                            >
                              <span className="block truncate">
                                {heading.text}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </div>
              )}

              {/* Related Articles placeholder */}
              <div className="p-4 rounded-lg border border-border bg-card">
                <h3 className="font-medium text-sm mb-3">Related Articles</h3>
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground">
                    Related articles will be shown here in a future update.
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Comments Panel/Drawer */}
      {slug && (
        <CommentsPanel
          articleSlug={slug}
          isOpen={isCommentsOpen}
          onClose={() => setIsCommentsOpen(false)}
          mode={window.innerWidth >= 1024 ? 'panel' : 'drawer'}
        />
      )}
    </div>
  );
}